/**
 * Browser-side trace context.
 *
 * Mirrors `@regta/common-backend/trace` (W3C traceparent) but in the
 * SPA. Mints a fresh trace id + span id per fetch, sends it as
 * `traceparent`, and surfaces it on errors so a failing request can
 * be matched to the matching server log by trace id.
 *
 * Deliberately tiny — no exporters, no spans-as-objects — just enough
 * to give every HTTP call a debug-friendly correlation id.
 */

function hex(bytes: number): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

export type ClientTrace = {
  traceparent: string;
  traceId: string;
  spanId: string;
};

export function newClientTrace(): ClientTrace {
  const traceId = hex(16);
  const spanId = hex(8);
  return {
    traceId,
    spanId,
    // sampled=01 — backend re-decides when it adds a sampler.
    traceparent: `00-${traceId}-${spanId}-01`,
  };
}

/** Most recent trace ids we've minted. Useful for the user copy-paste
 *  flow ("paste this into the bug report"); also used by error UIs. */
const recent: string[] = [];
const RECENT_MAX = 25;

export function rememberTrace(t: ClientTrace): void {
  recent.unshift(t.traceId);
  if (recent.length > RECENT_MAX) recent.length = RECENT_MAX;
}

export function recentTraceIds(): ReadonlyArray<string> {
  return recent;
}
